import { noop } from 'lodash';
import { createContext } from 'react';

import { useGuardedContext } from '@/hooks/useGuardedContext';

/**
 * UI state for the "Documents" step that must be shared between the step's Body
 * and Footer. The wizard renders those as siblings into separate DOM regions
 * (DialogContent vs DialogActions), so a context lets them coordinate the
 * recommendations panel and persist-once guard.
 */
export interface DocumentUploadStepContextValue {
  /**
   * The household created by step 1. Null until step 1 completes; guaranteed
   * set by the time the document-upload step is shown. Threaded through here
   * because the wizard renders the step's Body and Footer without props.
   */
  householdId: string | null;

  /**
   * Closes and resets the whole onboarding wizard. Used by exit actions after
   * persisting. Threaded from the wizard because step bodies have no props.
   */
  closeWizard: () => void;

  /**
   * Whether the modal has been closed at any point since this session opened.
   * Async continuations (e.g. a persist resolving) check this so they only
   * advance/navigate if the window was never closed — a mounted check isn't
   * enough because the footer stays mounted during the dialog's exit
   * transition. Reset per open-session (the provider is keyed on open).
   */
  hasBeenClosed: () => boolean;

  /** Whether the "Recommended documents" panel is expanded. */
  isRecommendationsExpanded: boolean;
  toggleRecommendations: () => void;
  /**
   * Force the recommendations panel open (idempotent). Used to auto-expand it
   * the first time an upload starts; separate from `toggleRecommendations` so it
   * never collapses an already-open panel.
   */
  expandRecommendations: () => void;

  /**
   * Synchronously claim, out of `fileIds`, the ones not yet persisted (or being
   * persisted) this session, marking them claimed and returning that subset.
   * Persistence is per-file rather than a one-shot session lock so files added
   * later (e.g. after going Back from a later step) still get saved, while the
   * non-idempotent `uploadDocuments` never creates the same file twice — even if
   * two callers persist concurrently, only the first claims each file.
   */
  claimForPersist: (fileIds: string[]) => string[];
  /** Release a claim (call after a failed save) so the files can be retried. */
  releaseClaim: (fileIds: string[]) => void;

  /**
   * Register an in-flight save so other callers can wait for it. Needed because
   * a claim is synchronous: once files are claimed a concurrent persist sees
   * nothing to claim, so a caller that navigates on "success" must still wait
   * for the claiming save to actually finish (see {@link awaitInFlightPersists}).
   */
  trackPersist: (run: Promise<boolean>) => void;
  /**
   * Resolves once every in-flight save has finished, `true` only if they all
   * succeeded. A caller that leaves the flow on success awaits this so it never
   * navigates away while a save (e.g. the background persister's) is still
   * pending and could yet fail.
   */
  awaitInFlightPersists: () => Promise<boolean>;
  /** Record and read the backend Documents created during this session. */
  recordPersistedDocumentIds: (documentIds: string[]) => void;
  getPersistedDocumentIds: () => string[];

  /**
   * Mark that the user has committed to their uploads (pressed Continue or an
   * exit action). Until then nothing is persisted, so uploads the user later
   * abandons by closing the wizard never become orphan Documents.
   */
  markCommitted: () => void;
  hasCommitted: () => boolean;
}

export const DocumentUploadStepContext =
  createContext<DocumentUploadStepContextValue>({
    householdId: null,
    closeWizard: noop,
    hasBeenClosed: () => false,
    isRecommendationsExpanded: false,
    toggleRecommendations: noop,
    expandRecommendations: noop,
    claimForPersist: () => [],
    releaseClaim: noop,
    trackPersist: noop,
    awaitInFlightPersists: () => Promise.resolve(true),
    recordPersistedDocumentIds: noop,
    getPersistedDocumentIds: () => [],
    markCommitted: noop,
    hasCommitted: () => false,
  });

export const useDocumentUploadStepContext = () =>
  useGuardedContext(DocumentUploadStepContext, 'DocumentUploadStepProvider');
